<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { DatePicker } from 'v-calendar'
import UiModalBottom from '@boats/ui/components/UiModalBottom.vue'

type Props = {
  label: string
  disabledDates?: any
  minDate: any
  maxDate: any
  fullWidth?: boolean
  class?: string
  modelValue: any
  error?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits(['update:modelValue'])

const classes = computed(() => ({
  'app-select-date': true,
  'app-select-date--full-width': props.fullWidth,
  [`${props.class}`]: !!props.class,
}))

const originalValue = ref(props.modelValue)

const modalOpen = ref(false)

const openModal = () => {
  originalValue.value = props.modelValue
  modalOpen.value = true
}

const closeModal = () => {
  emit('update:modelValue', originalValue.value)
  modalOpen.value = false
}

const submitModal = () => {
  modalOpen.value = false
  originalValue.value = props.modelValue
}

const closeModalOnDesktop = () => {
  const mql = window.matchMedia('(min-width: 768px)')

  if (mql.matches) {
    modalOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('resize', closeModalOnDesktop)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', closeModalOnDesktop)
})

const updateValue = (value: any) => {
  emit('update:modelValue', value)
}
</script>

<template>
  <div :class="classes">
    <div class="hidden w-full md:block">
      <DatePicker
        mode="date"
        :disabled-dates="disabledDates"
        :min-date="minDate"
        :max-date="maxDate"
        :model-value="modelValue"
        timezone="UTC"
        color="red"
        @update:model-value="updateValue"
      >
        <template #default="{ togglePopover }">
          <AppButtonSelect :full-width="fullWidth" @click="togglePopover" :class="[{'app-select-date-field--error': error}]">
            {{ modelValue ? formatDate(modelValue) : label }}
            <template #icon><IconCalendar /></template>
          </AppButtonSelect>
        </template>
      </DatePicker>
    </div>

    <AppButtonSelect
      :full-width="fullWidth"
      class="md:!hidden"
      @click="openModal"
    >
      {{ modelValue ? formatDate(modelValue) : label }}
      <template #icon><IconClock /></template>
    </AppButtonSelect>

    <UiModalBottom v-model="modalOpen" class="app-select__modal">
      <template #header>
        <span class="app-select__modal-title">{{ label }}</span>
      </template>

      <DatePicker
        mode="date"
        :disabled-dates="disabledDates"
        :min-date="minDate"
        :max-date="maxDate"
        :model-value="modelValue"
        timezone="UTC"
        color="red"
        title-position="left"
        transparent
        borderless
        expanded
        class="mb-10"
        @update:model-value="updateValue"
      ></DatePicker>

      <div class="row mt-auto">
        <div class="col-6">
          <AppButton variant="outlined" full-width @click="closeModal">
            {{ $t('string.Close') }}
          </AppButton>
        </div>

        <div class="col-6">
          <AppButton full-width @click="submitModal">
            {{ $t('string.Select') }}
          </AppButton>
        </div>
      </div>
    </UiModalBottom>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-select-date {
  position: relative;
  display: inline-flex;

  // --vc-white: #{$white};
  // --vc-black: #{$black};
  // --vc-font-family: #{$font-barlow};
  // --vc-text-sm: 0.75rem;

  // .vc-light.vc-attr,
  // .vc-light .vc-attr {
  //   --vc-highlight-solid-bg: #{$red};
  //   --vc-highlight-solid-content-color: #{$white};
  // }

  // .vc-light {
  //   --vc-color: #{$gray-400};
  //   --vc-bg: #{$white};
  //   --vc-border: #{$gray-200};
  //   --vc-hover-bg: #{$gray-100};
  //   --vc-focus-ring: 0 0 0 0.125rem $gray-200;
  //   --vc-header-arrow-color: #{$gray-400};
  //   --vc-header-arrow-hover-bg: #{$gray-100};
  //   --vc-header-title-color: #{$gray-400};
  //   --vc-weekday-color: #{$gray-400};
  //   --vc-weeknumber-color: #{$gray-400};
  //   --vc-nav-hover-bg: #{$gray-100};
  //   --vc-nav-title-color: #{$gray-400};
  //   --vc-nav-item-hover-box-shadow: none;
  //   --vc-nav-item-active-color: #{$black};
  //   --vc-nav-item-active-bg: #{$gray-100};
  //   --vc-nav-item-active-box-shadow: none;
  //   --vc-popover-content-color: #{gray-400};
  //   --vc-popover-content-bg: #{$white};
  //   --vc-popover-content-border: #{$gray-100};
  //   --vc-select-color: #{$white};
  //   --vc-select-bg: #{$tint-red-hover};
  //   --vc-select-hover-bg: #{$tint-red-hover};
  //   --vc-day-content-hover-bg: #{$red};
  //   --vc-day-content-disabled-color: #{$gray-200};
  // }

  .vc-weekday {
    padding-top: 0.625rem;
    padding-bottom: 0.625rem;
    font-family: $font-poppins;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.25rem;
  }

  .vc-day-content {
    // width: 2.5rem;
    // height: 2.5rem;
    // line-height: 2.5rem;
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.08333333em;

    &:hover {
      // color: $white;
    }
  }

  // .vc-highlight {
  //   width: 2.5rem;
  //   height: 2.5rem;
  // }

  .vc-header {
    margin-bottom: 0.75rem;
  }

  .vc-title {
    font-family: $font-poppins;
    font-size: 1rem;
    font-weight: 500;
  }

  .vc-nav-title {
    font-family: $font-poppins;
    font-weight: 600;
  }

  .vc-nav-item {
    font-family: $font-poppins;
    font-weight: 500;
  }

  .vc-prev {
    margin-right: 0.75rem;
  }
}

.app-select-date--full-width {
  display: flex;
  width: 100%;
}

.app-select-date__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  // width: 18.75rem;
  margin: 0;
  padding: 0.75rem;
  color: $black;
  list-style: none;
  background-color: $white;
  border: 1px solid $gray-100;
  border-radius: 0.5rem;
  box-shadow: 0.125rem 0.625rem 2rem 0 rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.app-select-date__modal {
  .ui-modal-bottom__content {
    display: flex;
    flex-direction: column;
    padding: 0.75rem;
  }
}

.app-select-date__modal-title {
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.015em;
  color: $black;
}

.app-select-date-field--error {
  background-color: $white;
  border-color: $signal-error-red;
}

.app-select-date__modal-options {
  margin: 0 0 1.5rem;
  padding: 0;
  list-style: none;
  border-top: 1px solid $gray-200;
}
</style>
